import { defineStore } from 'pinia'
import type Field from '@/types/field'
import { urlHashHandler } from '@/utils/urlHashHandler'
import { useProjectStore } from '@/store/project'
import { useFilterStore } from '@/store/filter'
import { useUnitStore } from '@/store/unit'
import _ from 'lodash'
import { ProjectFieldSort } from '@/types/field'
import type Unit from '~/types/unit'

interface SortState {
    field: Field
    desc: boolean
    locale: undefined | any
    customSorters: undefined | any
}

export const useSortStore = defineStore('sortStore', () => {
    const { $getI18n } = useNuxtApp()

    const sortableFields = ref<Field[]>([])

    const sortBy = ref<string | null>(null)
    const sortOrder = ref<string>('asc')

    const sort = ref({
        field: null,
        desc: false,
        locale: undefined,
        customSorters: undefined
    })

    const sortUnits = (units: Unit[]) => {
        if (!sortBy.value) {
            return units
        }

        return _.orderBy(units, [sortBy.value], [sortOrder.value === 'desc' ? 'desc' : 'asc'])
    }

    const getSortFieldFromUrl = () => {
        const sortField = urlHashHandler.hasKey('s:field') ? urlHashHandler.getKey('s:field') : null
        return sortField
    }

    const getSortOrderFromUrl = () => {
        const desc = urlHashHandler.hasKey('s:desc')
            ? urlHashHandler.getKey('s:desc') === 'true'
            : false

        return desc
    }

    const getProjectDefaultSortField = () => {
        const defaultSortField = sortableFields.value.find(
            (f) => f.sortable === ProjectFieldSort.asc || f.sortable === ProjectFieldSort.desc
        )

        return defaultSortField
    }

    const setSortState = (sortField: Field | undefined) => {
        if (!sortField) {
            sortBy.value = null
            sortOrder.value = 'asc'
            return
        }
        sortBy.value = sortField.key
        sortOrder.value = sortField.sortable
    }

    const setSortParametersToUrl = (sortField: Field) => {
        if (!sortField) return
        urlHashHandler.setKey('s:field', sortField.key)
        urlHashHandler.setKey('s:desc', sortField.sortable === 'desc')
    }

    const getCurrentSortStatus = () => {
        let sortOrder

        const sortField = getSortFieldFromUrl()
        if (sortField) {
            sortOrder = getSortOrderFromUrl()
            return { sortField, sortOrder }
        }

        return undefined
    }

    const isValidSortOrderField = (fieldKey: string) => {
        return sortableFields.value.find((f) => f.key === fieldKey)
    }

    const initializeSort = () => {
        sortableFields.value = useProjectStore().sortableFields

        let sortField

        const currentSortStatus = getCurrentSortStatus()

        if (currentSortStatus && isValidSortOrderField(currentSortStatus.sortField as string)) {
            const sortOrder = currentSortStatus.sortOrder ? 'desc' : 'asc'

            sortField = {
                ...sortableFields.value.find((f) => f.key === currentSortStatus.sortField),
                sortable: sortOrder
            }
        } else {
            sortField = getProjectDefaultSortField()
        }

        setSortParametersToUrl(sortField)
        setSortState(sortField)
    }

    const updateSort = (key: string, sortOrder: string) => {
        if (!key) unsetSort()
        const sortField = sortableFields.value.find((f) => f.key === key)

        if (!sortField) unsetSort()
        setSortParametersToUrl({ ...sortField, sortable: sortOrder })
        setSortState({ ...sortField, sortable: sortOrder })
    }

    const unsetSort = () => {
        setSortState(undefined)
        urlHashHandler.unsetKey('s:field')
        urlHashHandler.unsetKey('s:desc')
    }

    const getSortFunction = (type) => {
        switch (type) {
            case 'string':
            case 'html':
            case 'url':
                return stringCompare($getI18n)
            case 'boolean':
                return booleanCompare
            case 'date':
                return dateCompare
            // case 'number':
            // case 'currency':
            // case 'area':
            //     return numberCompare
            default:
                return undefined
        }
    }

    // const numberCompare = (a, b) => {
    //     let compareA = parseFloat(a)
    //     let compareB = parseFloat(b)

    //     if (Number.isNaN(compareA)) {
    //         compareA = 0
    //     }

    //     if (Number.isNaN(compareB)) {
    //         compareB = 0
    //     }

    //     if (compareA === compareB) {
    //         return 0
    //     } else if (compareA > compareB) {
    //         return 1
    //     } else if (compareA < compareB) {
    //         return -1
    //     }
    // }

    const stringCompare = (getI18n) => (a, b) => {
        let aString = a || ''
        let bString = b || ''
        if (typeof a === 'object') {
            aString = getI18n(aString)
        }
        if (typeof b === 'object') {
            bString = getI18n(bString)
        }
        return aString.localeCompare(bString)
    }

    const booleanCompare = (a, b) => {
        if (a === b) {
            return 0
        } else if (a === true || b === false) {
            return 1
        } else if (a === false || b === true) {
            return -1
        }
    }
    const dateCompare = (a, b) => {
        if (a && b) {
            const aT = new Date(a).getTime()
            const bT = new Date(b).getTime()
            return aT > bT ? 1 : aT < bT ? -1 : 0
        } else if (a || b) {
            return 1
        }
    }

    return {
        sortableFields,
        sortBy,
        sortOrder,
        initializeSort,
        updateSort,
        getSortFunction,
        sortUnits
    }
})
